import React from 'react';
import { 
  Table, 
  Button, 
  Card, 
  Tag, 
  Space, 
  message,
  Popconfirm,
  Typography
} from 'antd';
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined,
  DollarOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../components/MainLayout';
import { ApiConfig } from '../../services/ApiConfigs';
import planService from '../../services/planService';
import { routes } from '../../utils/routes.config';
import useFetch from '../../Hooks/FetchData';

const { Title, Text } = Typography;

const Plans = () => {
  const navigate = useNavigate();

  const { data: plans, loading, refetch } = useFetch(ApiConfig.PLANS, { trigger: true });

  const handleDelete = async (id: number) => {
    try {
      const res = await planService.deletePlan(id);
      if (res?.success !== false) {
        message.success(res?.message || 'Plan deleted successfully');
        refetch();
      } else {
        message.error(res?.message || 'Failed to delete plan');
      }
    } catch (error: any) {
      message.error(error?.response?.data?.message || error.message || 'Failed to delete plan');
    }
  };

  const handleEdit = (plan: any) => {
    navigate(`/admin/plans/edit/${plan.id}`);
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text: string, record: any) => (
        <Space>
          <strong>{text}</strong>
          {record.isPopular && <Tag color="gold">Popular</Tag>}
          {record.planType === 'one_time' ? (
            <Tag color="purple">One-Time</Tag>
          ) : (
            <Tag color="blue">Subscription</Tag>
          )}
        </Space>
      ),
    },
    {
      title: 'Price',
      key: 'price',
      render: (_: any, record: any) => (
        <Text strong>
          {record.currencySymbol} {record.price}
          {record.planType === 'subscription' && ` / ${record.billingPeriod}`}
          {record.planType === 'one_time' && ' (One-Time)'}
        </Text>
      ),
    },
    {
      title: 'Limits',
      key: 'limits',
      render: (_: any, record: any) => (
        <Space direction="vertical" size="small">
          <Text type="secondary">Warehouses: {record.maxWarehouses}</Text>
          <Text type="secondary">Drivers: {record.maxDrivers}</Text>
          {record.maxOrders && <Text type="secondary">Orders: {record.maxOrders}/month</Text>}
        </Space>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Action',
      key: 'action',
      render: (_: any, record: any) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this plan?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
            >
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <MainLayout>
      <Card
        title={
          <Space>
            <DollarOutlined />
            <Title level={4} style={{ margin: 0 }}>Plans Management</Title>
          </Space>
        }
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate(routes.PLAN_CREATE)}
          >
            Add Plan
          </Button>
        }
      >
        <Table
          columns={columns}
          dataSource={plans || []}
          loading={loading}
          rowKey="id"
          pagination={{ pageSize: 10 }}
        />
      </Card>
    </MainLayout>
  );
};

export default Plans;
